import moment from 'moment';
import { PV, FV } from '@formulajs/formulajs';

import { formatCurrency } from './util';
import { apiFetch } from './fetch';

const EFFECTIVE_TAX_RATE = 0.0;
const INFLATION_RATE = 0.025;

const PLAN_RECOVERY_RATE = 0.065;
const PLAN_RECOVERY_YEARS = 25;
const PROJECTION_PERIOD = 25;

export function getStats(client) {
  const leg1Compounded = !!client.leg1_compounded;
  const leg2Compounded = !!client.leg2_compounded;
  const leg3Compounded = !!client.leg3_compounded;
  const leg1YearsDeffered = Number(client.leg1_years_deferred) || 0;
  const leg2YearsDeffered = Number(client.leg2_years_deferred) || 0;
  const leg3YearsDeffered = Number(client.leg3_years_deferred) || 0;
  const effectiveTaxRate = Number(client.effective_tax_rate) || EFFECTIVE_TAX_RATE;
  const { assets_year_forms, income_year_forms, income_plan_forms } = client;

  const assetsNQ = client.assets?.filter((a) => a.type === 'NON_QUALIFIED');
  const assetsTD = client.assets?.filter((a) => a.type === 'TAX_DEFFERED');
  const assetsTF = client.assets?.filter((a) => a.type === 'TAX_FREE');

  let yearsToRetirement = 0;
  let displayName = '';
  let totalRetAssets = 0;
  let spouseYearsToRetirement = '';
  let age = 0;
  let spouseAge = '';
  const emergencyFunds = Number(client.emergency_fund) || 100000;

  const pensionAge = Number(client.pension_age) || 0;
  const spousePensionAge = Number(client.spouse_pension_age) || 0;
  const ssAge = Number(client.ss_age) || 0;
  const spouseSsAge = Number(client.spouse_ss_age) || 0;

  if (client.dob) {
    const dob = moment(client.dob);
    age = moment().diff(dob, 'years');
    if (client.spouse_dob) {
      const spouseDob = moment(client.spouse_dob);
      spouseAge = moment().diff(spouseDob, 'years');
    }
  }
  if (age && client.retirement_age) {
    yearsToRetirement = client.retirement_age - age;
    if (yearsToRetirement < 0) {
      yearsToRetirement = 'Retired';
    }
  }
  if (spouseAge && client.spouse_retirement_age) {
    spouseYearsToRetirement = client.spouse_retirement_age - spouseAge;
    if (spouseYearsToRetirement < 0) {
      spouseYearsToRetirement = 'Retired';
    }
  }

  if (client.lastname === client.spouse_lastname) {
    displayName = `${client.firstname} & ${client.spouse_firstname} ${client.lastname}`;
  }
  else if (client.spouse_firstname) {
    displayName = `${client.firstname} ${client.lastname} & ${client.spouse_firstname} ${client.spouse_lastname}`;
  } else {
    displayName = `${client.firstname} ${client.lastname}`;
  }
  totalRetAssets = client.assets.reduce((acc, a) => acc + (Number(a.value) || 0), 0);

  let homeEquityDisp = '';
  const heNum = (client.home_equity || 0) - (client.home_mortgage || 0);
  if (heNum < 0) {
    homeEquityDisp = '-' + formatCurrency(heNum);
  } else {
    homeEquityDisp = formatCurrency(heNum);
  }

  return {
    leg1Compounded,
    leg2Compounded,
    leg3Compounded,
    leg1YearsDeffered,
    leg2YearsDeffered,
    leg3YearsDeffered,
    effectiveTaxRate,
    assetsNQ,
    assetsTD,
    assetsTF,
    yearsToRetirement,
    displayName,
    totalRetAssets,
    spouseYearsToRetirement,
    age,
    spouseAge,
    emergencyFunds,
    homeEquityDisp,
    assets_year_forms,
    income_year_forms,
    income_plan_forms,
    pensionAge,
    spousePensionAge,
    ssAge,
    spouseSsAge,
  };
}

export async function toggleForm(user, client, formName, show) {
  client[formName] = show;
  try {
    const saved = await apiFetch.put(`/users/${user.id}/ip_households/${client.id}`, {
      [formName]: show,
    });
    saved.assets = client.assets;
    return saved;
  } catch (err) {
    console.error(`Error toggling form ${formName}:`, err);
    return client;
  }
}

export async function generateIncomeByYear(client) {
  try {
    const {
      leg1Compounded,
      leg2Compounded,
      leg3Compounded,
      leg1YearsDeffered,
      leg2YearsDeffered,
      leg3YearsDeffered,
      effectiveTaxRate,
      displayName,
      totalRetAssets,
      age,
      spouseAge,
      pensionAge,
      spousePensionAge,
      ssAge,
      spouseSsAge,
      emergencyFunds,
    } = getStats(client);

    const inflation = (Number(client.inflation_rate) / 100) || INFLATION_RATE;
  
    const pensionIncome = Number(client.pension) || 0;
    const spousePensionIncome = Number( client.spouse_pension) || 0;
    const rentalIncome = Number(client.rental) || 0;
    const spouseRentalIncome = Number(client.spouse_rental) || 0;
    const ssiIncome = Number(client.social_security) || 0;
    const spouseSsiIncome = Number(client.spouse_social_security) || 0;
    const desiredIncome = Number(client.desired_ati) || 0;
    const retirementAge = client.retirement_age || 65;
  
    client.leg1_age_to_begin = client.leg1_age_to_begin || age + 1;
    client.leg2_age_to_begin = client.leg2_age_to_begin || age + 6;
    client.leg3_age_to_begin = client.leg3_age_to_begin || age + 11;
  
    const planRecoveryGoal = totalRetAssets;
    const recoveryPrincipal = PV((client.assumed_net_growth / 100), PLAN_RECOVERY_YEARS, 0, planRecoveryGoal)*-1;
    const planIncomePrincipal = totalRetAssets - emergencyFunds - recoveryPrincipal;
  
    const leg1Principal = client.leg1_principle || 0;
    const leg2Principal = client.leg2_principle || 0;
    const leg3Principal = client.leg3_principle || 0;
    
    const leg1BonusPct = (Number(client.leg1_income_bonus_rate) / 100) || 0;
    const leg2BonusPct = (Number(client.leg2_income_bonus_rate) / 100) || 0;
    const leg3BonusPct = (Number(client.leg3_income_bonus_rate) / 100) || 0;
    const leg1RollupPct = (Number(client.leg1_rider_annual_increase) / 100) || 0;
    const leg2RollupPct = (Number(client.leg2_rider_annual_increase) / 100) || 0;
    const leg3RollupPct = (Number(client.leg3_rider_annual_increase) / 100) || 0;
  
    const leg1WithdrawalRate = (Number(client.leg1_withdrawal_rate) / 100) || 0;
    const leg2WithdrawalRate = (Number(client.leg2_withdrawal_rate) / 100) || 0;
    const leg3WithdrawalRate = (Number(client.leg3_withdrawal_rate) / 100) || 0;
    // =-fv(plan.leg1rollup,plan.leg1yrs,0,plan.leg1prin*(1+plan.leg1bonus)*plan.leg1wr)
    const leg1CompoundedTotal = -FV(leg1RollupPct, leg1YearsDeffered, 0, client.leg1_principle * (1 + leg1BonusPct) * leg1WithdrawalRate);
    const leg2CompoundedTotal = -FV(leg2RollupPct, leg2YearsDeffered, 0, client.leg2_principle * (1 + leg2BonusPct) * leg2WithdrawalRate);
    const leg3CompoundedTotal = -FV(leg3RollupPct, leg3YearsDeffered, 0, client.leg3_principle * (1 + leg3BonusPct) * leg3WithdrawalRate);
  
    // =(plan.leg1prin*(1+plan.leg1bonus)+(plan.leg1prin*(1+plan.leg1bonus))*plan.leg1rollup*plan.leg1yrs)*plan.leg1wr
    const leg1SimpleTotal = (client.leg1_principle * (1 + leg1BonusPct) + (client.leg1_principle * (1 + leg1BonusPct)) * leg1RollupPct * leg1YearsDeffered) * leg1WithdrawalRate;
    const leg2SimpleTotal = (client.leg2_principle * (1 + leg2BonusPct) + (client.leg2_principle * (1 + leg2BonusPct)) * leg2RollupPct * leg2YearsDeffered) * leg2WithdrawalRate;
    const leg3SimpleTotal = (client.leg3_principle * (1 + leg3BonusPct) + (client.leg3_principle * (1 + leg3BonusPct)) * leg3RollupPct * leg3YearsDeffered) * leg3WithdrawalRate;
  
    const leg1AnnualIncome = leg1Compounded ? leg1CompoundedTotal : leg1SimpleTotal;
    const leg2AnnualIncome = leg2Compounded ? leg2CompoundedTotal : leg2SimpleTotal;
    const leg3AnnualIncome = leg3Compounded ? leg3CompoundedTotal : leg3SimpleTotal;

    let rows = Array(PROJECTION_PERIOD).fill(0);
    const currentYear = client.plan_year;
    let data = rows.map((row, index) => {
      const year = currentYear + index;
      const yearAge = age + index;
      const yearSpouseAge = spouseAge ? spouseAge + index : '';
      return { year, yearAge, yearSpouseAge };
    });
    
    data.forEach((row, index) => {
      const { year, yearAge, yearSpouseAge } = row;
      const retired = yearAge >= client.retirement_age;
      const spouseRetired = yearSpouseAge >= client.spouse_retirement_age;
      const onSS = yearAge >= (ssAge || client.retirement_age);
      const onPension = yearAge >= (pensionAge || client.retirement_age);
      let spouseOnSS = false;
      let spouseOnPension = false;
      if (yearSpouseAge >= 0) {
        spouseOnSS = yearSpouseAge >= (spouseSsAge || client.spouse_retirement_age);
        spouseOnPension = yearSpouseAge >= (spousePensionAge || client.spouse_retirement_age);
      }
      row.yearRentalIncome = 0;
      row.yearSpouseRentalIncome = 0;
      row.yearPensionIncome = 0;
      row.yearSpousePensionIncome = 0;
      
      if (index === 0) {
        // first year, use current income values
        row.yearW2Income = client.current_income || 0;
        row.yearW22Income = client.spouse_current_income || 0;
        row.yearDesiredIncome = desiredIncome;
        if (rentalIncome) {
          row.yearRentalIncome = rentalIncome;
        }
        if (spouseRentalIncome) {
          row.yearSpouseRentalIncome = spouseRentalIncome;
        }
      } else {
        row.yearW2Income = retired ? 0 : data[index - 1].yearW2Income * (1 + inflation);
        row.yearW22Income = spouseRetired ? 0 : data[index - 1].yearW22Income * (1 + inflation);
        row.yearDesiredIncome = data[index - 1].yearDesiredIncome * (1 + inflation);
        if (rentalIncome) {
          row.yearRentalIncome = data[index - 1].yearRentalIncome * (1 + inflation);
        }
        if (spouseRentalIncome) {
          row.yearSpouseRentalIncome = data[index - 1].yearSpouseRentalIncome * (1 + inflation);
        }
      }
      
      row.yearSSI = 0;
      row.yearSSI2 = 0;
      row.yearSSITaxRate = 0;

      row.yearPensionIncome = 0;
      row.yearSpousePensionIncome = 0;

      if (onSS && ssiIncome) {
        if (index === 0) {
          row.yearSSI = ssiIncome;
        } else {
          if (data[index - 1].yearSSI) {
            row.yearSSI = data[index - 1].yearSSI * (1 + inflation);
          } else {
            row.yearSSI = ssiIncome;
          }
        }
      }

      if (spouseOnSS && spouseSsiIncome) {
        if (index === 0) {
          row.yearSSI2 = spouseSsiIncome;
        } else {
          if (data[index - 1].yearSSI2) {
            row.yearSSI2 = data[index - 1].yearSSI2 * (1 + inflation);
          } else {
            row.yearSSI2 = spouseSsiIncome;
          }
        }
      }

      if (onPension && pensionIncome) {
        row.yearPensionIncome = pensionIncome;
      }

      if (spouseOnPension && spousePensionIncome) {
        row.yearSpousePensionIncome = spousePensionIncome;
      }
      
      row.baseIncome = Number(row.yearW2Income)
        + Number(row.yearW22Income)
        + Number(row.yearPensionIncome)
        + Number(row.yearRentalIncome)
        + Number(row.yearSpousePensionIncome)
        + Number(row.yearSpouseRentalIncome);
      row.yearProvisionalIncome = Number(row.baseIncome) + ((row.yearSSI + row.yearSSI2) * 0.5);

      row.leg1Income = 0;
      row.leg2Income = 0;
      row.leg3Income = 0;
      if (row.year >= (client.plan_year + leg1YearsDeffered)) {
        row.leg1Income = leg1AnnualIncome;
      }
      if (row.year >= (client.plan_year + leg2YearsDeffered)) {
        row.leg2Income = leg2AnnualIncome;
      }
      if (row.year >= (client.plan_year + leg3YearsDeffered)) {
        row.leg3Income = leg3AnnualIncome;
      }

      if (!client.leg1_tax_free) {
        row.yearProvisionalIncome += row.leg1Income;
      }
      if (!client.leg2_tax_free) {
        row.yearProvisionalIncome += row.leg2Income;
      }
      if (!client.leg3_tax_free) {
        row.yearProvisionalIncome += row.leg3Income;
      }
      
      // Taxable Social Security via the IRS sliding scale (Form 1040 Worksheet 1).
      // Only provisional income OVER each statutory threshold contributes, capped at
      // 50%/85% of total benefits. The MIN wrappers prevent the old snap behavior that
      // overstated tax (and thus the apparent income gap) for clients near a threshold.
      const totalSSI = row.yearSSI + (Number(row.yearSSI2) || 0);
      let taxableSSI = 0;
      if (retired || spouseRetired) {
        // Single and HoH share the same statutory thresholds.
        const [t1, t2, tier1Cap] = client.filing_status === 'MFJ'
          ? [32000, 44000, 6000]
          : [25000, 34000, 4500];
        const pi = row.yearProvisionalIncome;
        if (pi <= t1) {
          taxableSSI = 0;
        } else if (pi <= t2) {
          taxableSSI = Math.min(totalSSI * 0.5, (pi - t1) * 0.5);
        } else {
          taxableSSI = Math.min(totalSSI * 0.85, (pi - t2) * 0.85 + Math.min(tier1Cap, totalSSI * 0.5));
        }
      }

      row.yearTaxableSSI = taxableSSI;
      // Effective % of benefits that are taxable, derived from the dollar amount above.
      row.yearSSITaxRate = totalSSI > 0 ? taxableSSI / totalSSI : 0;
      row.yearTaxableIncome = row.baseIncome + row.yearTaxableSSI;
      row.yearEstimatedTax = row.yearTaxableIncome * (effectiveTaxRate / 100);

      row.baseIncomeAfterTax = (row.baseIncome + row.yearSSI + row.yearSSI2) - row.yearEstimatedTax;
      row.yearGap = Number(row.yearDesiredIncome) - row.baseIncomeAfterTax;
      
      row.planIncomeTaxes = 0;
      if (!client.leg1_tax_free) {
        row.planIncomeTaxes += (effectiveTaxRate / 100) * row.leg1Income;
      }
      if (!client.leg2_tax_free) {
        row.planIncomeTaxes += (effectiveTaxRate / 100) * row.leg2Income;
      }
      if (!client.leg3_tax_free) {
        row.planIncomeTaxes += (effectiveTaxRate / 100) * row.leg3Income;
      }
      row.planIncomeTaxes *= -1;
      
      row.gap = row.yearDesiredIncome - row.planIncomeAfterTax;
      row.totalPlanIncomeAfterTax = row.baseIncomeAfterTax + row.leg1Income + row.leg2Income + row.leg3Income + row.planIncomeTaxes;
      row.planGap = row.totalPlanIncomeAfterTax - row.yearDesiredIncome;
    });

    const generatedClient = {
      ...client,
      incomeByYearData: data,
      displayName,
      age,
    };

    return generatedClient;
  } catch (err) {
    console.error(`Error generating income by year for client ${client.id}:`, err);
    return null;
  }
}

export async function generateRedesignedIncomePlan(client) {
  try {
    const {
      leg1Compounded,
      leg2Compounded,
      leg3Compounded,
      displayName,
      totalRetAssets,
      age,
      spouseAge,
    } = getStats(client);

    const emergencyFunds = Number(client?.emergency_fund) || 100000;
    
    client.leg1_age_to_begin = client.leg1_age_to_begin || age + 1;
    client.leg2_age_to_begin = client.leg2_age_to_begin || age + 6;
    client.leg3_age_to_begin = client.leg3_age_to_begin || age + 11;

    const planRecoveryGoal = totalRetAssets;
    const recoveryPrincipal = PV(PLAN_RECOVERY_RATE, PLAN_RECOVERY_YEARS, 0, planRecoveryGoal)*-1;
    client.income_bucket = client.income_bucket || totalRetAssets - (emergencyFunds || 0);
    const planIncomePrincipal = client.income_bucket;
    const growthPrincipal = totalRetAssets - (emergencyFunds || 0) - client.income_bucket;

    client.leg1_principle = client.leg1_principle || 0;
    client.leg2_principle = client.leg2_principle || 0;
    client.leg3_principle = client.leg3_principle || 0;

    const leg1BonusPct = (Number(client.leg1_income_bonus_rate) / 100) || 0;
    const leg2BonusPct = (Number(client.leg2_income_bonus_rate) / 100) || 0;
    const leg3BonusPct = (Number(client.leg3_income_bonus_rate) / 100) || 0;
    const leg1RollupPct = (Number(client.leg1_rider_annual_increase) / 100) || 0;
    const leg2RollupPct = (Number(client.leg2_rider_annual_increase) / 100) || 0;
    const leg3RollupPct = (Number(client.leg3_rider_annual_increase) / 100) || 0;
    const leg1Years = client.leg1_years_deferred || 0;
    const leg2Years = client.leg2_years_deferred || 0;
    const leg3Years = client.leg3_years_deferred || 0;
    const leg1WithdrawalRate = (Number(client.leg1_withdrawal_rate) / 100) || 0;
    const leg2WithdrawalRate = (Number(client.leg2_withdrawal_rate) / 100) || 0;
    const leg3WithdrawalRate = (Number(client.leg3_withdrawal_rate) / 100) || 0;
    const leg1RiderRate = (Number(client.leg1_rider_rate) / 100) || 0;
    const leg2RiderRate = (Number(client.leg2_rider_rate) / 100) || 0;
    const leg3RiderRate = (Number(client.leg3_rider_rate) / 100) || 0;

    const leg1CompoundedTotal = -FV(leg1RollupPct, leg1Years, 0, client.leg1_principle * (1 + leg1BonusPct) * leg1WithdrawalRate);
    const leg2CompoundedTotal = -FV(leg2RollupPct, leg2Years, 0, client.leg2_principle * (1 + leg2BonusPct) * leg2WithdrawalRate);
    const leg3CompoundedTotal = -FV(leg3RollupPct, leg3Years, 0, client.leg3_principle * (1 + leg3BonusPct) * leg3WithdrawalRate);

    const leg1SimpleTotal = (client.leg1_principle * (1 + leg1BonusPct) + (client.leg1_principle * (1 + leg1BonusPct)) * leg1RollupPct * leg1Years) * leg1WithdrawalRate;
    const leg2SimpleTotal = (client.leg2_principle * (1 + leg2BonusPct) + (client.leg2_principle * (1 + leg2BonusPct)) * leg2RollupPct * leg2Years) * leg2WithdrawalRate;
    const leg3SimpleTotal = (client.leg3_principle * (1 + leg3BonusPct) + (client.leg3_principle * (1 + leg3BonusPct)) * leg3RollupPct * leg3Years) * leg3WithdrawalRate;

    const leg1AnnualIncome = leg1Compounded ? leg1CompoundedTotal : leg1SimpleTotal;
    const leg2AnnualIncome = leg2Compounded ? leg2CompoundedTotal : leg2SimpleTotal;
    const leg3AnnualIncome = leg3Compounded ? leg3CompoundedTotal : leg3SimpleTotal;

    const recoveryAssets = FV((client.assumed_net_growth || 0) / 100, client.years_to_grow || 0, 0, growthPrincipal);

    let homeEquityDisp = 0;
    const heNum = (client.home_equity || 0) - (client.home_mortgage || 0);
    if (heNum < 0) {
      homeEquityDisp = '-' + formatCurrency(heNum);
    } else {
      homeEquityDisp = formatCurrency(heNum);
    }

    const pieChartTotal = Number(emergencyFunds) + Number(planIncomePrincipal) + Number(growthPrincipal);
    const additionalNeedsPercentage = ((Number(emergencyFunds) / pieChartTotal) * 100).toFixed(0);
    const pieChartIncomePercentage = ((Number(planIncomePrincipal) / pieChartTotal) * 100).toFixed(0);
    const pieChartGrowthPercentage = ((Number(growthPrincipal) / pieChartTotal) * 100).toFixed(0);

    const generatedClient = {
      ...client,
      leg1Compounded,
      leg2Compounded,
      leg3Compounded,
      displayName,
      totalRetAssets,
      age,
      spouseAge,
      emergencyFunds,
      planRecoveryGoal,
      recoveryPrincipal,
      planIncomePrincipal,
      growthPrincipal,
      leg1BonusPct,
      leg2BonusPct,
      leg3BonusPct,
      leg1RollupPct,
      leg2RollupPct,
      leg3RollupPct,
      leg1Years,
      leg2Years,
      leg3Years,
      leg1WithdrawalRate,
      leg2WithdrawalRate,
      leg3WithdrawalRate,
      leg1RiderRate,
      leg2RiderRate,
      leg3RiderRate,
      leg1CompoundedTotal,
      leg2CompoundedTotal,
      leg3CompoundedTotal,
      leg1SimpleTotal,
      leg2SimpleTotal,
      leg3SimpleTotal,
      leg1AnnualIncome,
      leg2AnnualIncome,
      leg3AnnualIncome,
      recoveryAssets,
      homeEquityDisp,
      additionalNeedsPercentage,
      pieChartIncomePercentage,
      pieChartGrowthPercentage,
    };

    return generatedClient;
  } catch (err) {
    console.error(`Error generating redesigned income plan for client ${client.id}:`, err);
    return null;
  }
}

export async function generateAssetsByYear(client) {
  try {
    const {
      leg1Compounded,
      leg2Compounded,
      leg3Compounded,
      yearsToRetirement,
      displayName,
      totalRetAssets,
      age,
      spouseAge,
      emergencyFunds,
    } = getStats(client);

    const inflation = (Number(client.inflation_rate) / 100) || INFLATION_RATE;

    client.leg1_age_to_begin = client.leg1_age_to_begin || age + 1;
    client.leg2_age_to_begin = client.leg2_age_to_begin || age + 6;
    client.leg3_age_to_begin = client.leg3_age_to_begin || age + 11;

    const retContributions = Number(client.ret_contributions) || 0;
    const spouseRetContributions = Number(client.spouse_ret_contributions) || 0;

    const leg1Principal = Number(client.leg1_principle) || 0;
    const leg2Principal = Number(client.leg2_principle) || 0;
    const leg3Principal = Number(client.leg3_principle) || 0;

    const leg1BonusPct = (Number(client.leg1_income_bonus_rate) / 100) || 0;
    const leg2BonusPct = (Number(client.leg2_income_bonus_rate) / 100) || 0;
    const leg3BonusPct = (Number(client.leg3_income_bonus_rate) / 100) || 0;
    const leg1RollupPct = (Number(client.leg1_rider_annual_increase) / 100) || 0;
    const leg2RollupPct = (Number(client.leg2_rider_annual_increase) / 100) || 0;
    const leg3RollupPct = (Number(client.leg3_rider_annual_increase) / 100) || 0;
    const leg1Years = Number(client.leg1_years_deferred) || 0;
    const leg2Years = Number(client.leg2_years_deferred) || 0;
    const leg3Years = Number(client.leg3_years_deferred) || 0;
    const leg1WithdrawalRate = (Number(client.leg1_withdrawal_rate) / 100) || 0;
    const leg2WithdrawalRate = (Number(client.leg2_withdrawal_rate) / 100) || 0;
    const leg3WithdrawalRate = (Number(client.leg3_withdrawal_rate) / 100) || 0;

    const leg1CompoundedTotal = -FV(leg1RollupPct, leg1Years, 0, leg1Principal * (1 + leg1BonusPct) * leg1WithdrawalRate);
    const leg2CompoundedTotal = -FV(leg2RollupPct, leg2Years, 0, leg2Principal * (1 + leg2BonusPct) * leg2WithdrawalRate);
    const leg3CompoundedTotal = -FV(leg3RollupPct, leg3Years, 0, leg3Principal * (1 + leg3BonusPct) * leg3WithdrawalRate);

    const leg1SimpleTotal = (leg1Principal * (1 + leg1BonusPct) + (leg1Principal * (1 + leg1BonusPct)) * leg1RollupPct * leg1Years) * leg1WithdrawalRate;
    const leg2SimpleTotal = (leg2Principal * (1 + leg2BonusPct) + (leg2Principal * (1 + leg2BonusPct)) * leg2RollupPct * leg2Years) * leg2WithdrawalRate;
    const leg3SimpleTotal = (leg3Principal * (1 + leg3BonusPct) + (leg3Principal * (1 + leg3BonusPct)) * leg3RollupPct * leg3Years) * leg3WithdrawalRate;

    const leg1AnnualIncome = leg1Compounded ? leg1CompoundedTotal : leg1SimpleTotal;
    const leg2AnnualIncome = leg2Compounded ? leg2CompoundedTotal : leg2SimpleTotal;
    const leg3AnnualIncome = leg3Compounded ? leg3CompoundedTotal : leg3SimpleTotal;

    client.income_bucket = Number(client.income_bucket) || (totalRetAssets - emergencyFunds);
    const growthPrincipal = totalRetAssets - emergencyFunds - client.income_bucket;

    const planYear = Number(client.plan_year) || moment().year();
    const rows = Array.from({ length: PROJECTION_PERIOD }).map((_, index) => {
      const year = planYear + index;
      const yearAge = age + index;
      const yearSpouseAge = spouseAge ? spouseAge + index : '';
      return { year, yearAge, yearSpouseAge };
    });

    rows.forEach((row, index) => {
      const { year } = row;
      row.newContributions = 0;

      if (index === 0) {
        row.leg1Principal = leg1Principal;
        row.leg2Principal = leg2Principal;
        row.leg3Principal = leg3Principal;
        row.growthPrincipal = growthPrincipal;
        if (index < yearsToRetirement) {
          row.newContributions = retContributions + spouseRetContributions;
        }
      } else {
        const prev = rows[index - 1];
        if (index < yearsToRetirement) {
          row.newContributions = prev.newContributions * (1 + inflation);
        }
        const growthRate = (Number(client.accum_rate) || 0) / 100;
        row.leg1Principal = Math.max(0, prev.leg1Principal * (1 + growthRate) - (year < (planYear + leg1Years) ? 0 : leg1AnnualIncome));
        row.leg2Principal = Math.max(0, prev.leg2Principal * (1 + growthRate) - (year < (planYear + leg2Years) ? 0 : leg2AnnualIncome));
        row.leg3Principal = Math.max(0, prev.leg3Principal * (1 + growthRate) - (year < (planYear + leg3Years) ? 0 : leg3AnnualIncome));
        row.growthPrincipal = prev.growthPrincipal * (1 + ((Number(client.assumed_net_growth) || 0) / 100)) + prev.newContributions;
      }

      row.yearTotalAssets = (row.leg1Principal || 0) + (row.leg2Principal || 0) + (row.leg3Principal || 0) + (row.growthPrincipal || 0) + emergencyFunds;
    });

    return {
      ...client,
      assetsByYearData: rows,
      displayName,
      age,
      spouseAge,
      emergencyFunds,
    };
  } catch (err) {
    console.error(`Error generating assets by year for client ${client.id}:`, err);
    return null;
  }
}

export async function generateReportsClient(client) {
  try {
    const stats = getStats(client);
    const incomeByYearData = await generateIncomeByYear(client);
    const redesignedIncomePlanData = await generateRedesignedIncomePlan(client);
    const assetsByYearData = await generateAssetsByYear(client);

    return {
      ...client,
      ...stats,
      ...incomeByYearData,
      ...redesignedIncomePlanData,
      ...assetsByYearData,
    };

  } catch (err) {
    console.error(`Error generating reports client for ${client.id}:`, err);
    return null;
  }
}